import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getCollectionHandle } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { from, switchMap } from 'rxjs';
import { KPF } from '../../master-data-management/kpf/entities/KPF.js';
import { FundingpotFunding } from '../../master-data-management/partner/entities/FundingpotFunding.js';
import { RAGFundingInformation } from '../entities/RAGFundingInformation.js';
import { createFundingsForKPF, getNumberOfPossibleFundingsForKPF } from './createFunding.js';

const getFundingExport = registerQuery({
  type: () => String,
  operationName: 'getFundingExport',
  resolve: FundingpotFunding.exportFundingsForKpfCSV,
});

const resolveSubscribeFunding = (partnerID: string) => (gqlFields: BaseQLSelectionSet<RAGFundingInformation>) => {
  const handle = getCollectionHandle<KPF>(KPF._collectionName);
  const selector = {};
  return from(publishFilter(KPF._class)).pipe(
    switchMap(publishFilter => {
      return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector);
    }),
    switchMap(async docs => {
      // filter information for partner on all KPFs
      docs.forEach(kpf => {
        kpf.ragPartnerInformation = kpf.ragPartnerInformation?.filter(
          information => information.ragPartnerIsID === partnerID || information.ragPartnerShouldID === partnerID,
        );
      });

      // All KPFs the partner is or should be funding in some way
      docs = docs.filter(kpf => kpf.ragPartnerInformation?.length);

      // Fill kpf field
      docs.forEach(kpf => {
        kpf.ragPartnerInformation?.forEach(info => {
          info.kpf = kpf;
        });
      });

      return docs.flatMap(kpf => kpf.ragPartnerInformation);
    }),
  );
};

const subscribeFundingsForPartner = registerSubscription({
  type: () => [RAGFundingInformation],
  operationName: 'subscribeFundingsForPartner',
  params: [
    {
      type: () => String,
      name: 'partnerID',
    },
  ],
  resolve: resolveSubscribeFunding,
});

export namespace FundingAPI {
  export const getExport = getFundingExport;
  export const subscribeForPartner = subscribeFundingsForPartner;
  export const createFundings = createFundingsForKPF;
  export const countPossibleFundings = getNumberOfPossibleFundingsForKPF;
}
